/**
 * Attachments section of the medical PA form: shows the clinical document card
 * (opens the viewer) or an empty state when no document is attached.
 *
 * @module medicalPaForm/components/medicalPaFormAttachments
 */
import RenderSectionHeader from "../../../components/formFields/renderSectionHeader";
import EyeIcon from "../../../svg/eye";
import WarningIcon from "../../../svg/warningIcon";

/** Props for {@link MedicalPaFormAttachments}. */
export interface MedicalPaFormAttachmentsProps {
  /** Firebase storage path of the clinical document, or "" when absent. */
  clinicalDocumentFilePath: string;
  /** Invoked with the section key when the section is clicked. */
  onSectionClick?: (sectionKey: string) => void;
  /** Opens the clinical document viewer (also logs the analytics event). */
  onViewDocument: () => void;
}

/**
 * Renders the attachments block, wiring the document card to the viewer.
 *
 * @param props - See {@link MedicalPaFormAttachmentsProps}.
 * @returns The attachments section element.
 */
const MedicalPaFormAttachments = ({
  clinicalDocumentFilePath,
  onSectionClick,
  onViewDocument,
}: MedicalPaFormAttachmentsProps) => {
  return (
    <div
      key={"attachments"}
      id={"attachments"}
      className="single-section__container mb-6"
      onClick={(e) => {
        e.stopPropagation();
        onSectionClick?.("attachments");
      }}
    >
      <RenderSectionHeader
        sectionTitle={"Attachments"}
        sectionKey={"attachments"}
        id={"attachments"}
      />
      {clinicalDocumentFilePath ? (
        <>
          <div
            className="document-card flex w-full justify-between rounded border border-primaryGray-15 px-3 py-2 text-small font-semibold shadow hover:cursor-pointer"
            onClick={onViewDocument}
          >
            <div className="document-card--text">Clinical Document</div>
            <div className="document-card--icon flex items-center gap-1 text-tertiaryBlue-4">
              <EyeIcon />
              View Document
            </div>
          </div>
        </>
      ) : (
        <>
          {" "}
          <div className="document-card flex w-full items-center gap-2 px-3 py-2 text-small font-normal text-tertiaryRed-3">
            <div className="document-card--info">
              <WarningIcon height="16" width="16" />
            </div>
            <div className="document-card--text">No Attachments Found</div>
          </div>
        </>
      )}
    </div>
  );
};

export default MedicalPaFormAttachments;
